import React from "react";

/* ---------- Basic primitives ---------- */

export function Button({ className = "", children, variant = "solid", size, ...rest }) {
  const classes = [
    "btn",
    variant === "outline" ? "btn-outline" : variant === "ghost" ? "btn-ghost" : "btn-solid",
    size === "sm" ? "btn-sm" : "",
    size === "icon" ? "btn-icon" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type="button" className={classes} {...rest}>
      {children}
    </button>
  );
}

export function Card({ className = "", children, ...rest }) {
  return (
    <div className={["card", className].filter(Boolean).join(" ")}>
      {children}
    </div>
  );
}

export function CardHeader({ className = "", children, ...rest }) {
  return (
    <div className={["card-header", className].filter(Boolean).join(" ")} {...rest}>
      {children}
    </div>
  );
}

export function CardTitle({ className = "", children, ...rest }) {
  return (
    <h3 className={["card-title", className].filter(Boolean).join(" ")} {...rest}>
      {children}
    </h3>
  );
}

export function CardContent({ className = "", children, ...rest }) {
  return (
    <div className={["card-content", className].filter(Boolean).join(" ")} {...rest}>
      {children}
    </div>
  );
}

export function Badge({ className = "", children, intent = "muted", ...rest }) {
  return (
    <span
      className={["badge", `badge-${intent}`, className].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </span>
  );
}

export function Input({ className = "", ...rest }) {
  return <input className={["input", className].filter(Boolean).join(" ")} {...rest} />;
}

/* ---------- Drawer (sheet) ---------- */

export function Sheet({ open, children }) {
  return <div data-open={open}>{children}</div>;
}

export function SheetTrigger({ children, onClick }) {
  return (
    <div onClick={onClick} role="button" className="sheet-trigger">
      {children}
    </div>
  );
}

export function SheetContent({ className = "", children, ...rest }) {
  return (
    <div className={["sheet-content", className].filter(Boolean).join(" ")} {...rest}>
      {children}
    </div>
  );
}

export function SheetHeader({ className = "", children }) {
  return (
    <div className={["sheet-header", className].filter(Boolean).join(" ")}>
      {children}
    </div>
  );
}

export function SheetTitle({ className = "", children, ...rest }) {
  return (
    <h2 className={["sheet-title", className].filter(Boolean).join(" ")} {...rest}>
      {children}
    </h2>
  );
}

/* ---------- Progress ring ---------- */

export function ProgressRing({
  percentage,
  size = 64,
  strokeWidth = 8,
  ariaLabel,
  stroke = "#10b981",
}) {
  const rawPercentage = Number.isFinite(percentage) ? Number(percentage) : 0;
  const clampedPercentage = Math.max(0, Math.min(100, rawPercentage));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clampedPercentage / 100) * circumference;

  return (
    <div
      role="img"
      aria-label={ariaLabel || `${Math.round(rawPercentage)}%`}
      className="ring-wrap"
    >
      <svg width={size} height={size} className="ring-svg">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="none"
        />
      </svg>
      <span className="ring-label">{Math.round(rawPercentage)}%</span>
    </div>
  );
}

/* ---------- Tile ---------- */

/**
 * Tile
 * - Always shows the meta icon (if provided).
 * - Only shows the Details toggle when details exist and detailsAlwaysExpanded is false.
 * - Renders action buttons when onOpen/onLog handlers are provided.
 * - Accepts className to allow per-tile styling (e.g. BMI tile).
 */
export function Tile({
  title,
  titleIcon,
  onTitleIconClick,
  badge,
  hero,
  helper,
  details,
  onOpen,
  onLog,
  metaIcon,
  onMetaIconClick,
  detailsAlwaysExpanded = false,
  className = "",
}) {
  const [isExpanded, setIsExpanded] = React.useState(detailsAlwaysExpanded);

  React.useEffect(() => {
    if (detailsAlwaysExpanded) {
      setIsExpanded(true);
    }
  }, [detailsAlwaysExpanded]);

  const handleMetaIconClick = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof onMetaIconClick === "function") {
      onMetaIconClick(event);
    }
  };

  const hasDetails = Boolean(details);
  const hasMetaControls = Boolean(metaIcon) || (hasDetails && !detailsAlwaysExpanded);
  const shouldRenderToggle = hasDetails && !detailsAlwaysExpanded;
  const detailsSectionIsExpanded = detailsAlwaysExpanded || isExpanded;

  const cardClassName = ["h-full", className].filter(Boolean).join(" ");

  return (
    <Card className={cardClassName}>
      <CardHeader className="tile-header">
        <CardTitle className="tile-title">
          <span>{title}</span>
          {titleIcon ? (
            onTitleIconClick ? (
              <button
                type="button"
                onClick={onTitleIconClick}
                style={{
                  marginLeft: "8px",
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                }}
                aria-label="More information"
              >
                {titleIcon}
              </button>
            ) : (
              <span style={{ marginLeft: "8px" }}>{titleIcon}</span>
            )
          ) : null}
        </CardTitle>
        {badge ? <Badge intent={badge.intent}>{badge.label}</Badge> : null}
      </CardHeader>

      <CardContent>
        <div className="tile-hero-row">
          <div className="tile-hero">{hero}</div>

          {hasMetaControls ? (
            <div className="desktop-meta-controls">
              {/* Meta icon is shown regardless of details */}
              {metaIcon ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="tile-meta-btn"
                  aria-label={`Go to ${title}`}
                  onClick={handleMetaIconClick}
                >
                  <span aria-hidden="true" className="tile-meta-icon">
                    {metaIcon}
                  </span>
                </Button>
              ) : null}

              {/* Only render the toggle if details exist and we are not always-expanded */}
              {shouldRenderToggle ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsExpanded((previousValue) => !previousValue)}
                  aria-label={detailsSectionIsExpanded ? "Hide details" : "Show details"}
                >
                  {detailsSectionIsExpanded ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 15l-6-6-6 6" />
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  )}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        {helper ? <p className="tile-helper">{helper}</p> : null}

        {(onOpen || onLog) ? (
          <div className="tile-actions">
            {onOpen ? (
              <Button variant="outline" className="flex-1" onClick={onOpen}>
                Open
              </Button>
            ) : null}
            {onLog ? (
              <Button variant="outline" className="flex-1" onClick={onLog}>
                + Log
              </Button>
            ) : null}
          </div>
        ) : null}

        {hasDetails ? (
          <div className={`tile-details ${detailsSectionIsExpanded ? "expanded" : ""}`}>
            {details}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
